import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { Route } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store, select } from '@ngrx/store';
import Debug from 'debug';
import { BehaviorSubject, Observable, Subscription, distinctUntilChanged } from 'rxjs';
import { IPageHeader } from '../interfaces/pageHeader.interface';
import { AumicoCoreLayoutProvider } from '../layout/ngrx/aumico.layout.provider';
import {
    ActionHeaderService,
    AumGlobalAction,
    IAumGlobalActionEvent,
} from '../pattern/action-header/actionHeader.service';
import { AumBreadcrumbComponentItem } from '../pattern/breadcrumb/breadcrumb';
import { AumPaginationActionBar } from '../pattern/pagination-action-bar/pagination-action-bar';
import { ActionHeaderConfig } from '../pattern/public-apis';
import { AumicoCoreProvider } from '../provider/aumico.core.provider';
import { onMainContentChange } from './animations/animations';
import { AumicoLayoutConfig } from './interfaces/layout.config';
import {
    ModuleState,
    selectActivePage,
    selectActiveSubNavigation,
    selectLayoutIsHidden,
} from './ngrx/aumico.layout.reducer';
import { SidenavService } from './services/sidenav.service';

const debug = Debug('aumico:aumico-lib-core-fe:SidebarLayout');
const SUB_PAGE_BREAD_CRUMB_INDEX = 1;
const SUB_NAVIGATION = 'sub';
@UntilDestroy()
@Component({
    selector: 'app-sidebar-layout',
    templateUrl: './sidebar.layout.html',
    styleUrls: ['./sidebar.layout.scss'],
    animations: [onMainContentChange],
})
export class SidebarLayout implements OnInit, OnDestroy {
    static actionHeaderHasBackButton = new BehaviorSubject<boolean>(true);
    static actionText = new BehaviorSubject<string | null>(null);
    static filterText = new BehaviorSubject<string | null>(null);
    static searchText = new BehaviorSubject<string | null>(null);

    @Input() hideSubNavigation = false;
    // If initial true, it will be open
    public onSideNavChange = true;
    public layoutIsHidden$: Observable<boolean>;
    public activePage$: Observable<Route>;
    public activeSubNavigation$: Observable<Route>;
    public actionPageHeader$: Observable<IPageHeader>;
    private subscritions: Subscription[] = new Array<Subscription>();

    // Actionheader
    public actionHeaderHasBackButton$ = SidebarLayout.actionHeaderHasBackButton.asObservable();
    public actionText$ = SidebarLayout.actionText.asObservable();
    public filterText$ = SidebarLayout.filterText.asObservable();
    public searchText$ = SidebarLayout.searchText.asObservable();

    blockingRequestIsActive$: Observable<boolean>;
    hideFooterOption$: Observable<boolean>;

    breadcrumbItems: AumBreadcrumbComponentItem[] = [
        {
            displayName: 'Page',
            onClick: () => {
                debug('Page is clicked');
                debug(this);
            },
            path: '/page',
        },
        {
            displayName: 'SubPage',
        },
        {
            displayName: 'Current',
            isActive: true,
            onClick: () => {
                debug('Current is clicked');
                debug(this);
            },
            path: '/page/subPage/Current',
        },
    ];

    @ViewChild('aumpaginator', { static: false }) pagingActionBar: AumPaginationActionBar;

    static updateActionHeaderWithRoute(route: Route) {
        debug('fire update action header', (route.data as AumicoLayoutConfig)?.actionHeaderConfig);
        if ((route.data as AumicoLayoutConfig)?.actionHeaderConfig) {
            SidebarLayout.actionText.next((route.data as AumicoLayoutConfig)?.actionHeaderConfig.actionText!);
            SidebarLayout.filterText.next((route.data as AumicoLayoutConfig)?.actionHeaderConfig.filterText!);
            SidebarLayout.searchText.next((route.data as AumicoLayoutConfig)?.actionHeaderConfig.searchText!);
            SidebarLayout.actionHeaderHasBackButton.next(
                (route.data as AumicoLayoutConfig)?.actionHeaderConfig.hasBackButton!,
            );
        }
    }
    static updateActionHeaderWithConfig(actionHeaderConfig: ActionHeaderConfig) {
        debug('fire update action header with config', actionHeaderConfig);
        if (actionHeaderConfig) {
            SidebarLayout.actionText.next(actionHeaderConfig.actionText!);
            SidebarLayout.filterText.next(actionHeaderConfig.filterText!);
            SidebarLayout.searchText.next(actionHeaderConfig.searchText!);
            SidebarLayout.actionHeaderHasBackButton.next(actionHeaderConfig.hasBackButton!);
        }
    }

    // If initial true, it will be open
    constructor(
        private sidenavService: SidenavService,
        private matIconRegistry: MatIconRegistry,
        private domSanitizer: DomSanitizer,
        private actionHeaderService: ActionHeaderService,
        private layoutStore: Store<ModuleState>,
        private aumicoCoreLayoutProvider: AumicoCoreLayoutProvider,
        private coreProvider: AumicoCoreProvider,
    ) {
        this.addSvgIcon('dashboard');
        this.addSvgIcon('menu-close');
        this.addSvgIcon('logout');
        this.addSvgIcon('aumico-pattern-icon-add-circle');
        this.addSvgIcon('aumico-pattern-icon-remove-circle');
        this.addSvgIcon('aumico-pattern-icon-info');
        this.addSvgIcon('aumico-pattern-icon-alert-info');
        this.addSvgIcon('aumico-pattern-icon-settings-vertical');
        this.addSvgIcon('aumico-pattern-icon-arrow-up');
        this.addSvgIcon('aumico-pattern-icon-arrow-down');
        this.addSvgIcon('aumico-pattern-icon-arrow-right');
        this.addSvgIcon('aumico-pattern-icon-arrow-left');
        this.addSvgIcon('aumico-pattern-icon-view-off');
        this.addSvgIcon('aumico-pattern-icon-view-on');
        this.addSvgIcon('aumico-pattern-icon-filter');
        this.addSvgIcon('aumico-pattern-icon-outlook');
        this.addSvgIcon('aumico-pattern-icon-gmail');
        this.addSvgIcon('aumico-pattern-icon-yahoo');
        this.addSvgIcon('aumico-pattern-icon-clients');
        this.addSvgIcon('aumico-pattern-icon-annualreports');
        this.addSvgIcon('aumico-pattern-icon-companies');
        this.addSvgIcon('aumico-pattern-icon-data-file-check');
        this.addSvgIcon('aumico-pattern-icon-fcp');

        // even if it's used like that.
        this.layoutIsHidden$ = this.layoutStore.pipe(select(selectLayoutIsHidden));
        this.activePage$ = this.layoutStore.pipe(select(selectActivePage));
        this.activeSubNavigation$ = this.layoutStore.pipe(select(selectActiveSubNavigation));
        this.hideFooterOption$ = this.aumicoCoreLayoutProvider.getHideFooterOption$().pipe(untilDestroyed(this));

        this.blockingRequestIsActive$ = this.coreProvider.getBlockingRequestIsRunning$();
        this.actionPageHeader$ = aumicoCoreLayoutProvider.getPageHeader$();
    }

    ngOnInit() {
        // TODO: fix breadcrumb logic
        this.subscritions.push(
            this.aumicoCoreLayoutProvider.getActivePage().subscribe((activePage) => {
                debug('aumicoCoreLayoutProvider.getActivePage()', activePage);
                this.breadcrumbItems = [];
                if (activePage) {
                    this.breadcrumbItems.push({
                        displayName: (activePage.data as AumicoLayoutConfig)?.label,
                    });
                }
            }),
        );

        this.aumicoCoreLayoutProvider
            .getActiveSubNavigation()
            .pipe(distinctUntilChanged(), untilDestroyed(this))
            .subscribe((activeSubNavigation: Route) => {
                debug('aumicoCoreLayoutProvider.getActiveSubNavigation()', activeSubNavigation);
                if (
                    activeSubNavigation != null &&
                    (activeSubNavigation.data as AumicoLayoutConfig)?.type === SUB_NAVIGATION
                ) {
                    if (
                        this.breadcrumbItems.length > 0 &&
                        (activeSubNavigation.data as AumicoLayoutConfig)?.label != null
                    ) {
                        this.breadcrumbItems[SUB_PAGE_BREAD_CRUMB_INDEX] = {
                            displayName: (activeSubNavigation.data as AumicoLayoutConfig)?.label,
                        };
                    }
                }
            });
        this.subscritions.push(
            this.sidenavService.sideNavState$.subscribe((res) => {
                debug('sidenavService.sideNavState$', res);
                this.onSideNavChange = res;
            }),
        );
        this.subscritions.push(
            this.actionHeaderService.onActionEvent$.subscribe((action: IAumGlobalActionEvent) => {
                this.onAction(action);
            }),
        );
        this.subscritions.push(
            this.activePage$.subscribe((route: Route) => {
                this.onActivePageChange(route);
            }),
        );
        this.subscritions.push(
            this.activeSubNavigation$.subscribe((route: Route) => {
                this.onActiveSubPageChange(route);
            }),
        );
    }

    ngOnDestroy() {
        this.subscritions.forEach((sub) => {
            sub.unsubscribe();
        });
    }

    private updateBreadcrumb() {}

    private addSvgIcon(name: string) {
        this.matIconRegistry.addSvgIcon(
            name,
            this.domSanitizer.bypassSecurityTrustResourceUrl(`/assets/icons/${name}.svg`),
        );
    }

    private onAction(event: IAumGlobalActionEvent) {
        debug('actionHeaderService.onActionEvent$??', event);

        if (event && event.action === AumGlobalAction.ACTIONHEADERUPDATE) {
            SidebarLayout.updateActionHeaderWithConfig(event.value);
        }
    }

    private onActivePageChange(route: Route) {
        debug('onActivePageChange', route);

        let sub = [];
        // TODO make that flexbile that the route defines if there is a back navigation or not
        if ((route.data as AumicoLayoutConfig)?.type === 'primary') {
            if (route.children) {
                SidebarLayout.updateActionHeaderWithRoute(route);
                return;
            }
            if ((route.data as AumicoLayoutConfig)?.parent) {
                sub = (route.data as AumicoLayoutConfig)?.parent.children.filter(
                    (r: Route) => (r.data as AumicoLayoutConfig)?.type === 'sub',
                );
            }
        }
        if ((route.data as AumicoLayoutConfig)?.type === 'sub' || (route.data as AumicoLayoutConfig)?.type === 'main') {
            SidebarLayout.updateActionHeaderWithRoute((route.data as AumicoLayoutConfig)?.parent);
        } else {
            SidebarLayout.updateActionHeaderWithRoute(route);
        }
    }
    private onActiveSubPageChange(route: Route) {
        debug('onActiveSubPageChange', route);

        if (route === undefined) {
            SidebarLayout.actionHeaderHasBackButton.next(false);
        } else {
            SidebarLayout.updateActionHeaderWithRoute(route);
        }
    }

    onSinenavToggle($event) {
        debug($event);
        debug($event.target);
        if ($event.offsetX > $event.target.offsetWidth && $event.target.localName === 'mat-sidenav') {
            this.onSideNavChange = !this.onSideNavChange;
            this.sidenavService.sideNavState$.next(this.onSideNavChange);
        }
    }
}
